/**
 * Returns only the suffix portion of a composite SKU. When `basePrefix` (the
 * known base/default variant SKU) is passed, strips exactly that prefix +
 * separator — required whenever the base SKU can itself contain the
 * separator character (e.g. base "AB-12" with separator "-": stripping at
 * the *first* "-" would wrongly return "12-RED" instead of "RED" for
 * "AB-12-RED"). Without `basePrefix`, falls back to splitting at the first
 * occurrence of `separator`, which is only safe when the base SKU is known
 * not to contain it.
 * For `separator === ""` (no delimiter to split on), returns the value
 * unchanged unless `basePrefix` is given — there's no reliable way to
 * recover a suffix from a delimiter-free composite otherwise.
 * e.g. skuSuffix("62810-0_VAL-1556", "_") → "VAL-1556"
 */
export function skuSuffix(
  sku: string | null | undefined,
  separator = "_",
  basePrefix?: string | null,
): string {
  if (!sku) return sku ?? "";

  if (basePrefix) {
    const fullPrefix = `${basePrefix}${separator}`;

    return sku.startsWith(fullPrefix) ? sku.slice(fullPrefix.length) : sku;
  }

  if (!separator) return sku;

  const idx = sku.indexOf(separator);

  return idx === -1 ? sku : sku.slice(idx + separator.length);
}

/**
 * Sanitizes a user-typed SKU suffix and reconstructs the full
 * `{prefix}{separator}{suffix}` composite SKU, mirroring the derivation the
 * Go backend performs server-side (see saleor_variant_writer.go's
 * CreateVariantFromValue). Used by the variant grid / stocks form's SKU
 * cell-edit handlers so both share one tested implementation instead of
 * duplicating the split/sanitize/rejoin logic inline.
 *
 * For `separator === ""` there's no delimiter to recover a prefix from by
 * splitting `originalSku` — callers should pass the base variant's own known
 * SKU as `prefix` explicitly in that case (see ProductVariants.tsx /
 * ProductStocks.tsx for how the base SKU is looked up).
 */
export function reconstructSkuOnEdit(
  originalSku: string | null | undefined,
  typedSuffix: string,
  separator = "_",
  prefix?: string,
): string {
  const sanitized = typedSuffix.replace(/[^a-zA-Z0-9\-.]/g, "");

  const resolvedPrefix =
    prefix ??
    (separator && originalSku?.includes(separator) ? originalSku.split(separator)[0] : "");

  return resolvedPrefix ? `${resolvedPrefix}${separator}${sanitized}` : sanitized;
}
